import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { Combobox, type ComboboxOption } from './combobox';
import { PackagingUnitFields } from './packaging-unit-fields';
import type { PresetItem } from './types';

async function searchPresets(path: string, query: string): Promise<ComboboxOption[]> {
  const res = await apiFetch<{ items: PresetItem[] }>(`${path}?query=${encodeURIComponent(query)}`);
  return res.items.map((i) => ({ id: i.id, label: i.name }));
}
async function createPreset(path: string, name: string): Promise<ComboboxOption> {
  const res = await apiFetch<{ id: string }>(path, { method: 'POST', body: { name } });
  return { id: res.id, label: name };
}

/** S8 "新建产品"：只要求 name + baseUnit（Spec §9 REQUIRED），其余字段留给详情页补齐。 */
export function ProductCreatePage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [baseUnitId, setBaseUnitId] = useState('');
  const [brand, setBrand] = useState<ComboboxOption | null>(null);
  const [category, setCategory] = useState<ComboboxOption | null>(null);
  const [origin, setOrigin] = useState<ComboboxOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** 【用户三格式设计】第一层外包装（可空）+ 第二层基本单位 + 第三层每包装数量 */
  const [packagingUomId, setPackagingUomId] = useState('');
  const [packagingQty, setPackagingQty] = useState('');
  const qtyValid = /^[1-9]\d*$/.test(packagingQty.trim());

  // 【用户需求 2026-08-19】"上一个 SKU 是 339，我没改的话自动变 340"——
  // 只在字段还是初始空值时填一次；用户改过/清空过之后绝不再覆盖。
  const [skuAutoFilled, setSkuAutoFilled] = useState(false);
  const skuTouchedRef = useRef(false);
  const { data: skuSuggestion } = useQuery({
    queryKey: ['products', 'next-sku'],
    queryFn: () => apiFetch<{ suggestion: string | null }>('/products/next-sku'),
  });
  useEffect(() => {
    if (skuSuggestion?.suggestion && !skuTouchedRef.current) {
      setSku(skuSuggestion.suggestion);
      setSkuAutoFilled(true);
    }
  }, [skuSuggestion]);

  const create = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string }>('/products', {
        method: 'POST',
        body: {
          sku: sku.trim(),
          name: name.trim(),
          baseUnitId,
          brandId: brand?.id,
          categoryId: category?.id,
          originId: origin?.id,
          ...(packagingUomId && qtyValid
            ? { packaging: { uomId: packagingUomId, factorToBase: packagingQty.trim() } }
            : {}),
        },
      }),
    onSuccess: (res) => navigate(`/products/${res.id}`),
    onError: (err) =>
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err)),
  });

  return (
    <div className="card" style={{ maxWidth: '640px' }}>
      <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('products.create')}</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="field">
          <span>{t('products.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          <span>{t('products.sku')}</span>
          <input
            value={sku}
            onChange={(e) => {
              skuTouchedRef.current = true;
              setSkuAutoFilled(false);
              setSku(e.target.value);
            }}
            required
          />
          {skuAutoFilled && <span className="field-hint">{t('products.skuAutoSuggested')}</span>}
        </label>
        {/* 【用户三格式设计】箱（外包装）→ 支（基本单位）→ 24（每箱数量）＝ 一箱 24 支 */}
        <PackagingUnitFields
          baseUnitId={baseUnitId}
          onBaseUnitIdChange={setBaseUnitId}
          packagingUomId={packagingUomId}
          onPackagingUomIdChange={setPackagingUomId}
          packagingQty={packagingQty}
          onPackagingQtyChange={setPackagingQty}
        />
        <label className="field">
          <span>{t('products.brand')}</span>
          <Combobox
            value={brand}
            onChange={setBrand}
            fetchOptions={(q) => searchPresets('/brands', q)}
            onCreate={(n) => createPreset('/brands', n)}
            placeholder={t('products.pickBrand')}
          />
        </label>
        <label className="field">
          <span>{t('products.category')}</span>
          <Combobox
            value={category}
            onChange={setCategory}
            fetchOptions={(q) => searchPresets('/product-categories', q)}
            onCreate={(n) => createPreset('/product-categories', n)}
            placeholder={t('products.pickCategory')}
          />
        </label>
        <label className="field">
          <span>{t('products.origin')}</span>
          <Combobox
            value={origin}
            onChange={setOrigin}
            fetchOptions={(q) => searchPresets('/origins', q)}
            onCreate={(n) => createPreset('/origins', n)}
            placeholder={t('products.pickOrigin')}
          />
        </label>

        {error && <p className="field-error">{error}</p>}

        <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={
              create.isPending ||
              !name.trim() ||
              !sku.trim() ||
              !baseUnitId ||
              (packagingUomId !== '' && !qtyValid)
            }
          >
            {t('app.save')}
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => navigate('/products')}>
            {t('app.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
